import * as React from 'react';

import {Link} from '../../link/link';
import {Body} from '../../typography/body';

import {NUTSHELL_FEATURE_MAP} from './const';
import type {NutshellFeatures} from './types';

type RequirementProps = {
    text: string;
    link?: string;
    linkIntroText?: string;
    linkText?: string;
};

type NutshellTeamNoteProps = {
    feature: NutshellFeatures;
    integration: string;
    secondFeature?: NutshellFeatures;
};

const Requirement = (props: RequirementProps) => {
    const {text, link, linkIntroText = 'Don’t have one?', linkText = 'Sign up here.'} = props;

    return (
        <>
            {text}{' '}
            {link && (
                <Body>
                    {linkIntroText}{' '}
                    <Link href={link} newTab={true} variant='primary'>
                        {linkText}
                    </Link>
                </Body>
            )}
        </>
    );
};

const NutshellTeamNote = (props: NutshellTeamNoteProps) => {
    const {feature, integration, secondFeature} = props;

    // @ts-expect-error
    const featureLink = (featureKey, displayText) => (
        // @ts-expect-error
        <Link newTab={true} variant='primary' href={NUTSHELL_FEATURE_MAP[featureKey].featureLink}>
            {/* @ts-expect-error */}
            {displayText || NUTSHELL_FEATURE_MAP[featureKey].featureDisplayText}
        </Link>
    );

    // @ts-expect-error
    const learnMoreLink = (featureKey, text) => (
        // @ts-expect-error
        <Link newTab={true} variant='primary' href={NUTSHELL_FEATURE_MAP[featureKey].learnMoreLink}>
            {text}
        </Link>
    );

    if (!secondFeature) {
        return (
            <>
                {/* @ts-expect-error */}
                You can also use {featureLink(feature)}
                {feature === 'ProspectorIQ' ? ' to find prospects and replace ' : ' to replace '}
                {integration}
                {'! ('}
                {learnMoreLink(feature, 'Learn more')}
                {')'}
            </>
        );
    }

    return (
        <>
            {/* @ts-expect-error */}
            You can also use {featureLink(feature)}
            {' and '}
            {/* @ts-expect-error */}
            {featureLink(secondFeature)}
            {' to replace '}
            {integration}
            {'! ('}
            {learnMoreLink(feature, `Learn more about ${feature}`)}
            {' | '}
            {learnMoreLink(secondFeature, `Learn more about ${secondFeature}`)}
            {')'}
        </>
    );
};

export const INTEGRATIONS_DATA = [
    {
        integrationId: 'seamlessai',
        name: 'SeamlessAI',
        primaryText: 'Create new leads automatically from SeamlessAI contacts/companies',
        categoryId: ['lead_generation'],
        integrationActionItems: [
            'Create leads in Nutshell from new SeamlessAI contacts',
            'Create leads in Nutshell from new SeamlessAI companies',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/seamless-crm-integration.webp',
        requirements: [
            <Requirement
                key='1'
                text='A SeamlessAI account.'
                link='https://get.seamless.ai/fgpq29t4wike'
            />,
        ],
        noteFromNutshellTeam: [
            <NutshellTeamNote key='1' feature='ProspectorIQ' integration='SeamlessAI' />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/seamless-ai-integrations-banner.webp',
    },
    {
        integrationId: 'squarespace',
        name: 'Squarespace',
        primaryText: 'Create contacts and leads automatically',
        categoryId: ['forms'],
        integrationActionItems: [
            'Create or update contacts in Nutshell from new Squarespace form submissions',
            'Create leads in Nutshell from new Squarespace form submissions',
        ],
        integrationLogoUrl:
            '/include/images/integration-logos-new/squarespace-crm-integration.webp',
        requirements: [<Requirement key='1' text='A Squarespace account/website' />],
        noteFromNutshellTeam: [
            <NutshellTeamNote
                key='1'
                feature='Landing pages'
                secondFeature='Nutshell Forms'
                integration='Squarespace'
            />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/squarespace-integrations-banner.png',
    },
    {
        integrationId: 'convertkit',
        name: 'ConvertKit',
        primaryText: 'Create or update contacts without switching apps.',
        categoryId: ['lead_generation', 'forms'],

        integrationActionItems: [
            'Create or update contacts in Nutshell from new Kit (formerly ConvertKit) subscribers',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/kit-crm-integration.webp',
        requirements: [
            <Requirement
                key='1'
                text='A Kit account.'
                link='https://partners.kit.com/11o0c36ryt1j'
            />,
        ],
        noteFromNutshellTeam: [
            <NutshellTeamNote key='1' feature='Email marketing' integration='ConvertKit' />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/kit-integrations-banner.webp',
    },
    {
        integrationId: 'trello',
        name: 'Trello',
        primaryText: 'Create a Trello card when a Nutshell lead is created/won',
        categoryId: ['collaboration'],
        integrationActionItems: [
            'Create a Trello card when a Nutshell lead is created',
            'Create a Trello card when a Nutshell lead is won',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/trello-crm-integration.webp',
        requirements: [<Requirement key='1' text='A Trello account' />],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/trello-integrations-banner.png',
    },
    {
        integrationId: 'textiful',
        name: 'Textiful',
        primaryText: 'Create a contact or send a text without switching apps',
        categoryId: ['communication'],
        integrationActionItems: [
            'Create a new Nutshell contact from new Textiful contacts',
            'When a new Nutshell lead is created, send a Textiful text message',
            'When a new Nutshell lead is created, add to a Textiful texting list',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/textiful-crm-integration.png',
        integrationBannerUrl:
            '/include/images/integration-logo-banners/textiful-integrations-banner.webp',
        requirements: [
            <Requirement
                key='1'
                text='Requires a paid Textiful account. Contacts will have to reply “YES” to the opt-in text that is sent to them first.'
            />,
        ],
    },
    {
        integrationId: 'mightyforms',
        name: 'MightyForms',
        primaryText: 'Create new contacts/leads in Nutshell without switching apps',
        categoryId: ['forms', 'lead_generation'],
        integrationActionItems: [
            'Create new Nutshell contacts for new MightyForms submissions',
            'Create new Nutshell leads for new MightyForms submissions',
        ],
        integrationLogoUrl:
            '/include/images/integration-logos-new/mightyforms-crm-integration.webp',
        requirements: [<Requirement key='1' text='A MightyForms account' />],
        noteFromNutshellTeam: [
            <NutshellTeamNote key='1' feature='Nutshell Forms' integration='MightyForms' />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/mightyforms-integrations-banner.webp',
    },
    {
        integrationId: 'uncommongiving',
        name: 'Uncommon Giving',
        primaryText: 'Create new contacts from new gifts',
        categoryId: ['other'],
        integrationActionItems: ['Create new Nutshell contacts from new gifts in Uncommon Giving'],
        integrationLogoUrl: '/include/images/integration-logos-new/uncommon-crm-integration.webp',
        requirements: [<Requirement key='1' text='An Uncommon Giving account' />],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/uncommon-giving-integrations-banner.webp',
    },
    {
        integrationId: 'webflow',
        name: 'Webflow',
        primaryText: 'Create new contacts/leads from form submissions',
        categoryId: ['forms', 'lead_generation'],
        integrationActionItems: [
            'Create or update contacts in Nutshell from new Webflow form submissions',
            'Create leads in Nutshell from new Webflow form submissions',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/webflow-crm-integration.webp',
        requirements: [
            <Requirement
                key='1'
                text='A Webflow account.'
                link='https://try.webflow.com/6os51n4kr2hj'
            />,
        ],
        noteFromNutshellTeam: [
            <NutshellTeamNote
                key='1'
                feature='Landing pages'
                secondFeature='Nutshell Forms'
                integration='Webflow'
            />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/webflow-integrations-banner.webp',
    },
    {
        integrationId: 'apollo',
        name: 'Apollo',
        primaryText: 'Create new contacts in Nutshell automatically',
        categoryId: ['lead_generation'],
        integrationActionItems: ['Create Nutshell contacts for new Apollo contacts'],
        integrationLogoUrl: '/include/images/integration-logos-new/apollo-crm-integration.webp',
        requirements: [
            <Requirement
                key='1'
                text='An Apollo account.'
                link='https://get.apollo.io/r3wna0h1tj7w'
            />,
        ],
        noteFromNutshellTeam: [
            <NutshellTeamNote key='1' feature='ProspectorIQ' integration='Apollo' />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/apollo-integrations-banner.png',
    },
    {
        integrationId: 'ninjaforms',
        name: 'Ninja Forms',
        primaryText: 'Create/update new contacts & leads in Nutshell automatically',
        categoryId: ['forms'],
        integrationActionItems: [
            'Create or update contacts in Nutshell from new Ninja Forms form submissions',
            'Create leads in Nutshell from new Ninja Forms form submissions',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/ninjaforms-crm-integration.webp',
        requirements: [<Requirement key='1' text='A Ninja Forms account' />],
        noteFromNutshellTeam: [
            <NutshellTeamNote key='1' feature='Nutshell Forms' integration='Ninja Forms' />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/ninja-forms-integrations-banner.png',
    },
    {
        integrationId: 'wpforms',
        name: 'WPForms',
        primaryText: 'Create/update new contacts & Leads in Nutshell automatically',
        categoryId: ['forms'],
        integrationActionItems: [
            'Create or update contacts in Nutshell from new WPForms form submissions',
            'Create leads in Nutshell from new WPForms form submissions',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/wpforms-crm-integration.webp',
        requirements: [<Requirement key='1' text='A WPForms account' />],
        noteFromNutshellTeam: [
            <NutshellTeamNote key='1' feature='Nutshell Forms' integration='WPForms' />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/wp-forms-integrations-banner.webp',
    },
    {
        integrationId: 'callrail',
        name: 'CallRail',
        primaryText: 'Log a Nutshell activity when a CallRail phone call is completed',
        categoryId: ['instant', 'communication'],
        integrationActionItems: [
            'Log a Nutshell activity when a CallRail phone call is completed',
            'Create a lead in Nutshell when a CallRail phone call is completed',
        ],
        logoStyles: {width: 120, padding: '28px'},
        integrationLogoUrl: '/include/images/integration-logos-new/callrail-logo.png',
        requirements: [
            <Requirement
                key='1'
                text='A CallRail account.'
                link='https://partners.callrail.com/660kg3mfavub'
            />,
        ],
        noteFromNutshellTeam: [
            <NutshellTeamNote key='1' feature='Click-to-call' integration='CallRail' />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/callrail-integrations-banner.png',
    },
    {
        integrationId: 'aircall',
        name: 'Aircall',
        primaryText: 'Log a Nutshell activity when a Aircall phone call is completed',
        categoryId: ['instant', 'communication'],
        integrationActionItems: [
            'Log a Nutshell activity when a Aircall phone call is completed',
            'Create a lead in Nutshell when a Aircall phone call is completed',
        ],
        logoStyles: {width: 150},
        integrationLogoUrl: '/include/images/integration-logos-new/aircall-logo.png',
        requirements: [<Requirement key='1' text='A Aircall account.' link='https://aircall.io' />],
        noteFromNutshellTeam: [
            <NutshellTeamNote key='1' feature='Click-to-call' integration='Aircall' />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/aircall-integrations-banner.png',
    },
    {
        integrationId: 'airtable',
        name: 'Airtable',
        primaryText: 'Sync leads automatically without switching apps',
        categoryId: ['collaboration'],
        integrationActionItems: [
            'Create new Nutshell contacts from new records in Airtable',
            'Add new Nutshell leads to Airtable',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/airtable-crm-integration.webp',
        requirements: [<Requirement key='1' text='An Airtable account' />],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/airtable-integrations-banner.png',
    },
    {
        integrationId: 'acuityscheduling',
        name: 'Acuity Scheduling',
        primaryText: 'Create new contacts & leads from new appointments.',
        categoryId: ['scheduling'],
        integrationActionItems: [
            'Create new Nutshell contacts from new Acuity Scheduling appointments',
            'Create new Nutshell leads from new Acuity Scheduling appointments',
        ],
        integrationLogoUrl:
            '/include/images/integration-logos-new/acuity-scheduling-crm-integration.webp',
        requirements: [<Requirement key='1' text='An Acuity Scheduling account' />],
        noteFromNutshellTeam: [
            <NutshellTeamNote
                key='1'
                feature='Nutshell Scheduler'
                integration='Acuity Scheduling'
            />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/acuity-scheduling-integrations-banner.png',
    },
    {
        integrationId: 'quotient',
        name: 'Quotient',
        primaryText: 'Automatically create leads and log activities without switching apps',
        categoryId: ['lead_generation', 'contracts_proposals'],
        integrationActionItems: [
            'When a new Nutshell lead is created, create a lead in Quotient',
            'Create a new Nutshell lead when a lead is created in Quotient',
            'Update a Nutshell lead when a Quotient quote is accepted, sent or declined',
            'Log a Nutshell activity when a Quotient quote is sent',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/quotient-crm-integration.webp',
        requirements: [<Requirement key='1' text='A Quotient account' />],
        noteFromNutshellTeam: [
            <NutshellTeamNote
                key='1'
                feature='Nutshell Quotes & Invoices'
                integration='Quotient'
            />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/quotient-integrations-banner.png',
    },
    {
        integrationId: 'kixie',
        name: 'Kixie',
        primaryText: 'Log a Nutshell activity when a Kixie call is completed',
        categoryId: ['instant', 'communication'],
        integrationActionItems: [
            'Log a Nutshell activity when a Kixie call is completed',
            'Create a Kixie contact for new Nutshell contacts',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/kixie-crm-integration.webp',
        requirements: [
            <Requirement
                key='1'
                text='A Kixie account.'
                link='https://dash.partnerstack.com/kixie'
            />,
        ],
        noteFromNutshellTeam: [
            <NutshellTeamNote
                key='1'
                feature='Click-to-call'
                integration='Kixie'
                secondFeature='Nutshell SMS'
            />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/kixie-integrations-banner.png',
    },
    {
        integrationId: 'justcall',
        name: 'JustCall',
        primaryText: 'Automatically log an activity or send a text',
        categoryId: ['communication'],
        integrationActionItems: [
            'Log a Nutshell activity when a JustCall call ends',
            'Send a JustCall text message when a Nutshell lead is created',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/justcall-crm-integration.webp',
        requirements: [
            <Requirement
                key='1'
                text='A JustCall account.'
                link='http://justcall.io/?fp_ref=kevin42'
            />,
        ],
        noteFromNutshellTeam: [
            <NutshellTeamNote
                key='1'
                feature='Click-to-call'
                integration='JustCall'
                secondFeature='Nutshell SMS'
            />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/justcall-integrations-banner.png',
    },
    {
        integrationId: 'openphone',
        name: 'OpenPhone',
        primaryText: 'Automatically log an activity and sync contacts',
        categoryId: ['communication'],
        integrationActionItems: [
            'Log a Nutshell activity when an OpenPhone call completes',
            'Create an OpenPhone contact for new Nutshell contacts',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/openphone-crm-integration.webp',
        requirements: [
            <Requirement
                key='1'
                text='An OpenPhone account.'
                link='https://get.openphone.com/4gfu6n6dxdji'
            />,
        ],
        noteFromNutshellTeam: [
            <NutshellTeamNote
                key='1'
                feature='Click-to-call'
                integration='OpenPhone'
                secondFeature='Nutshell SMS'
            />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/openphone-integrations-banner.png',
    },
    {
        integrationId: 'tiktok',
        name: 'TikTok',
        primaryText: 'Create a new Nutshell lead from new leads received in TikTok',
        categoryId: ['communication'],
        integrationActionItems: ['Create a new Nutshell lead from new leads received in TikTok'],
        integrationLogoUrl: '/include/images/integration-logos-new/tiktok-crm-integration.webp',
        requirements: [
            <Requirement
                key='1'
                text='A TikTok account.'
                link='https://getstartedtiktok.pxf.io/c/5666224/1359578/16372'
                linkIntroText='To get started with advertising on TikTok,'
                linkText='sign up for TikTok for Business here.'
            />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/tiktok-integrations-banner.png',
    },
    {
        integrationId: 'woocommerce',
        name: 'WooCommerce',
        primaryText: 'Create a new Nutshell lead from new WooCommerce orders',
        categoryId: ['lead_generation'],
        integrationActionItems: ['Create a new Nutshell lead from new WooCommerce orders'],
        integrationLogoUrl:
            '/include/images/integration-logos-new/woocommerce-crm-integration.webp',
        requirements: [
            <Requirement key='1' text='An WooCommerce account' />,
            <Requirement key='2' text='The WooCommerce Zapier extensiont' />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/woo-integrations-banner.png',
    },
    {
        integrationId: 'shopify',
        name: 'Shopify',
        primaryText: 'Create leads from paid orders and abandoned carts',
        categoryId: ['lead_generation'],
        integrationActionItems: [
            'Create Nutshell contacts from new paid orders on Shopify',
            'Create Nutshell leads for abandoned Shopify carts',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/shopify-crm-integration.webp',
        requirements: [
            <Requirement key='1' text='A Shopify account.' link='https://shopify.pxf.io/Jz5zMQ' />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/shopify-integrations-banner.png',
    },
    {
        integrationId: 'eventbrite',
        name: 'eventbrite',
        primaryText: 'Create Nutshell contacts or leads from new Eventbrite attendees',
        categoryId: ['lead_generation', 'webinars'],
        integrationActionItems: ['Create Nutshell contacts or leads from new Eventbrite attendees'],
        integrationLogoUrl: '/include/images/integration-logos-new/eventbrite-crm-integration.webp',
        requirements: [<Requirement key='1' text='An Eventbrite account' />],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/eventbrite-integrations-banner.png',
    },
    {
        integrationId: 'linkedinads',
        name: 'LinkedIn Ads',
        primaryText: 'Create a new Nutshell lead from new leads received in LinkedIn',
        categoryId: ['lead_generation'],
        integrationActionItems: ['Create a new Nutshell lead from new leads received in LinkedIn'],
        integrationLogoUrl:
            '/include/images/integration-logos-new/linkedin-ads-crm-integration.webp',
        requirements: [<Requirement key='1' text='A LinkedIn Ads account' />],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/linkedin-ads-integrations-banner.png',
    },
    {
        integrationId: 'givebutter',
        name: 'GiveButter',
        primaryText: 'Create new Nutshell contacts from new transactions in GiveButter',
        categoryId: ['other'],
        integrationActionItems: [
            'Create new Nutshell contacts from new transactions in GiveButter',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/givebutter-crm-integration.webp',
        requirements: [<Requirement key='1' text='A GiveButter account' />],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/givebutter-integrations-banner.png',
    },
    {
        integrationId: 'leadpages',
        name: 'Leadpages',
        primaryText: 'Create/update contacts and leads in Nutshell from Leadpages submissions',
        categoryId: ['forms'],
        integrationActionItems: [
            'Create or update contacts in Nutshell from new LeadPages form submissions',
            'Create leads in Nutshell from new LeadPages form submissions',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/leadpages-crm-integration.webp',
        requirements: [
            <Requirement
                key='1'
                text='A Leadpages account.'
                link='https://try.leadpages.com/6we4r791m4lu'
            />,
        ],
        noteFromNutshellTeam: [
            <NutshellTeamNote key='1' feature='Landing pages' integration='Leadpages' />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/leadpages-integrations-banner.png',
    },
    {
        integrationId: 'facebookleadads',
        name: 'Facebook Lead Ads',
        primaryText: 'Create a new Nutshell lead from new leads received in Facebook lead ads',
        categoryId: ['lead_generation'],
        integrationActionItems: [
            'Create a new Nutshell lead from new leads received in Facebook lead ads',
        ],
        integrationLogoUrl:
            '/include/images/integration-logos-new/facebook-lead-crm-integration.png',
        requirements: [<Requirement key='1' text='A Facebook Lead Ads account' />],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/facebook-ads-integrations-banner.png',
    },
    {
        integrationId: 'ewebinar',
        name: 'eWebinar',
        primaryText: 'Create Nutshell contacts and log activities from new eWebinar registrations',
        categoryId: ['webinars'],
        integrationActionItems: [
            'Create Nutshell contacts from new eWebinar registrations',
            'Log Nutshell activities from eWebinar attendance',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/ewebinar-crm-integration.webp',
        requirements: [<Requirement key='1' text='An eWebinar account' />],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/e-webinar-integrations-banner.png',
    },
    {
        integrationId: 'demio',
        name: 'Demio',
        primaryText: 'Create Nutshell leads for Demio registrants',
        categoryId: ['webinars'],
        integrationActionItems: [
            'Create Nutshell leads for Demio registrants',
            'Log a Nutshell activity when a registrant joins a webcast',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/demio-crm-integration.webp',
        requirements: [
            <Requirement
                key='1'
                text='A Demio account.'
                link='https://demio.com/?fp_ref=nutshellcrm'
            />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/demio-integrations-banner.png',
    },
    {
        integrationId: 'stripe',
        name: 'Stripe',
        primaryText: 'Create Nutshell contacts or leads from new Stripe payments',
        categoryId: ['accounting'],
        integrationActionItems: ['Create Nutshell contacts or leads from new Stripe payments'],
        integrationLogoUrl: '/include/images/integration-logos-new/stripe-crm-integration.webp',
        requirements: [<Requirement key='1' text='A Stripe account' />],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/stripe-integrations-banner.webp',
    },
    {
        integrationId: 'ecologi',
        name: 'Ecologi',
        primaryText: 'When a Nutshell lead is won, plant a tree with Ecologi',
        categoryId: ['other'],
        integrationActionItems: ['When a Nutshell lead is won, plant a tree with Ecologi'],
        integrationLogoUrl: '/include/images/integration-logos-new/ecologi-crm-integration.webp',
        requirements: [<Requirement key='1' text='An Ecologi account' />],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/ecologi-integrations-banner.webp',
    },
    {
        integrationId: 'calendly',
        name: 'Calendly',
        primaryText: 'Create a lead when a booking is made',
        categoryId: ['collaboration'],
        integrationActionItems: [
            'Create Nutshell contacts from new Calendly invitees',
            'Create Nutshell leads from new Calendly invitees',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/calendly-crm-integration.webp',
        requirements: [<Requirement key='1' text='A Calendly account' />],
        noteFromNutshellTeam: [
            <NutshellTeamNote key='1' feature='Nutshell Scheduler' integration='Calendly' />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/calendly-integrations-banner.webp',
    },
    {
        integrationId: 'proposify',
        name: 'Proposify',
        primaryText: 'Update a Nutshell lead when a Proposify proposal is created, sent or signed',
        categoryId: ['contracts'],
        integrationActionItems: [
            'Update a Nutshell lead when a Proposify proposal is created, sent or signed',
            'Log a Nutshell activity when a Proposify quote is sent',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/proposify-crm-integration.webp',
        requirements: [<Requirement key='1' text='A Proposify account' />],
        noteFromNutshellTeam: [
            <NutshellTeamNote
                key='1'
                feature='Nutshell Quotes & Invoices'
                integration='Proposify'
            />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/proposify-integrations-banner.png',
    },
    {
        integrationId: 'perspective',
        name: 'Perspective',
        primaryText: 'Create a Nutshell lead from a Perspective lead',
        categoryId: ['lead_generation'],
        integrationActionItems: ['Create a Nutshell lead from a Perspective lead'],
        integrationLogoUrl:
            '/include/images/integration-logos-new/perspective-crm-integration.webp',
        requirements: [<Requirement key='1' text='A Perspective account' />],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/perspective-integrations-banner.webp',
    },
    {
        integrationId: 'freshcaller',
        name: 'Freshcaller',
        primaryText: 'Log a Nutshell activity when a FreshCaller call completes',
        categoryId: ['communication'],
        integrationActionItems: [
            'Log a Nutshell activity when a FreshCaller call completes',
            'Create a FreshCaller contact for new Nutshell contacts',
        ],
        integrationLogoUrl:
            '/include/images/integration-logos-new/freshcaller-crm-integration.webp',
        requirements: [<Requirement key='1' text='A FreshCaller account' />],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/freshcaller-integrations-banner.png',
    },
    {
        integrationId: 'mightycall',
        name: 'MightyCall',
        primaryText: 'Log a Nutshell activity when a MightyCall call completes',
        categoryId: ['communication'],
        integrationActionItems: [
            'Log a Nutshell activity when a MightyCall call completes',
            'Create a MightyCall contact for new Nutshell contacts',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/mightycall-crm-integration.webp',
        requirements: [
            <Requirement
                key='1'
                text='A MightyCall account.'
                link='http://mighty-call.sjv.io/5gXGN3'
            />,
        ],
        noteFromNutshellTeam: [
            <NutshellTeamNote key='1' feature='Click-to-call' integration='MightyCall' />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/mightycall-integrations-banner.webp',
    },
    {
        integrationId: 'docusign',
        name: 'DocuSign',
        primaryText: 'Update a Nutshell lead when a DocuSign envelope is sent or completed',
        categoryId: ['contracts'],
        integrationActionItems: [
            'Update a Nutshell lead when a DocuSign envelope is sent or completed',
            'Log a Nutshell activity when a DocuSign envelope is sent',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/docusign-crm-integration.webp',
        requirements: [<Requirement key='1' text='A DocuSign account' />],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/docusign-integrations-banner.png',
    },
    {
        integrationId: 'signnow',
        name: 'SignNow',
        primaryText:
            'Update a Nutshell lead & log an activity when a SignNow document is completed',
        categoryId: ['contracts'],
        integrationActionItems: [
            'Update a Nutshell lead when a SignNow document is completed',
            'Log a Nutshell activity when a SignNow document is completed',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/signnow-crm-integration.webp',
        requirements: [<Requirement key='1' text='A SignNow account' />],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/signnow-integrations-banner.webp',
    },
    {
        integrationId: 'signwell',
        name: 'SignWell',
        primaryText:
            'Update a Nutshell lead & log an activity when a SignWell document is completed',
        categoryId: ['contracts'],
        integrationActionItems: [
            'Update a Nutshell lead when a SignWell document is completed',
            'Log a Nutshell activity when a SignWell document is completed',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/signwell-crm-integration.webp',
        requirements: [<Requirement key='1' text='A SignWell account' />],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/signwell-integrations-banner.webp',
    },
    {
        integrationId: 'signrequest',
        name: 'SignRequest',
        primaryText:
            'Update a Nutshell lead & log an activity when a SignRequest document is signed',
        categoryId: ['contracts'],
        integrationActionItems: [
            'Update a Nutshell lead when a SignRequest document is signed',
            'Log a Nutshell activity when a SignRequest document is signed',
        ],
        integrationLogoUrl:
            '/include/images/integration-logos-new/signrequest-crm-integration.webp',
        requirements: [<Requirement key='1' text='A SignRequest account' />],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/signrequest-integrations-banner.png',
    },
    {
        integrationId: 'microsoft-teams-events',
        name: 'Microsoft Teams Events',
        primaryText: 'Create Nutshell leads for Microsoft Teams Events webinar registration',
        categoryId: ['webinars'],
        integrationActionItems: [
            'Create Nutshell leads for Microsoft Teams Events webinar registration',
        ],
        integrationLogoUrl:
            '/include/images/integration-logos-new/microsoft-teams-live-crm-integration.webp',
        requirements: [<Requirement key='1' text='A  Microsoft Teams account' />],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/microsoft-teams-live-events-integrations-banner.png',
    },
    {
        integrationId: 'dialpad',
        name: 'Dialpad',
        primaryText: 'Log a Nutshell activity when a Dialpad call is completed',
        categoryId: ['instant', 'communication'],
        integrationLogoUrl: '/include/images/integration-logos-new/dialpad-logo.jpg',
        logoStyles: {width: 120, padding: '16px'},
        requirements: [<Requirement key='1' text='A Pro or Enterprise Dialpad account' />],
        integrationActionItems: [
            'Log a Nutshell activity when a Dialpad call is completed',
            'Create a Dialpad contact for new Nutshell contacts',
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/dialpad-integrations-banner.webp',
    },
    {
        integrationId: 'dropbox-docsend',
        name: 'Dropbox DocSend',
        primaryText: 'Update timelines automatically when a customer takes an action',
        categoryId: ['contracts_proposals'],
        integrationActionItems: [
            'Add new DocSend visitor to Nutshell as contacts',
            'Add a note to a Nutshell contact’s timeline when a DocSend link is visited',
            'Add a note to a Nutshell contact’s timeline when a DocSend file is downloaded',
        ],
        integrationLogoUrl:
            '/include/images/integration-logos-new/dropbox-docsend-crm-integration.webp',
        requirements: [<Requirement key='1' text='An active DocSend account' />],
        supportedFields: ['Document name', 'Visitor name', 'Visitor email', 'Visit URL details'],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/dropbox-docsend-integrations-banner.png',
    },
    {
        integrationId: 'dropbox-sign',
        name: 'Dropbox Sign',
        primaryText: 'Send documents without switching apps',
        categoryId: ['contracts_proposals'],
        integrationActionItems: [
            'Email a document signing request when a lead is won',
            'Email a document signing request when a lead is created',
            'Mark a lead as won when a document has been signed',
        ],
        integrationLogoUrl:
            '/include/images/integration-logos-new/dropbox-sign-crm-integration.webp',
        requirements: [<Requirement key='1' text='An active Dropbox Sign account' />],
        supportedFields: ['Contact name', 'Company name', 'Custom fields'],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/dropbox-sign-integrations-banner.png',
    },
    {
        integrationId: 'google-sheets',
        name: 'Google Sheets',
        primaryText: 'Create new rows or leads without switching apps',
        categoryId: ['collaboration'],
        integrationActionItems: [
            'Add a row to a Google Sheet when a Nutshell lead is won',
            'Add a row to a Google Sheet when a Nutshell lead is created',
            'Create a Nutshell lead when a row is added to a Google Sheet',
        ],
        integrationLogoUrl:
            '/include/images/integration-logos-new/google-sheets-crm-integration.webp',
        requirements: [<Requirement key='1' text='A Google account' />],
        supportedFields: [
            'Lead name',
            'Phone/ Email / Address',
            'Lead value',
            'Assignee',
            'Pipeline',
            'Tags',
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/google-sheets-integrations-banner.webp',
    },
    {
        integrationId: 'gravity-forms',
        name: 'Gravity Forms',
        primaryText: 'Create leads and contacts, send drip campaigns',
        categoryId: ['forms'],
        integrationActionItems: [
            'Create a lead in Nutshell when a Gravity Form is submitted',
            'Create a contact in Nutshell when a Gravity Form is submitted',
            'Automatically send a Nutshell drip email when a Gravity Form is submitted',
        ],
        integrationLogoUrl:
            '/include/images/integration-logos-new/gravity-forms-crm-integration.webp',
        requirements: [
            <Requirement
                key='1'
                text='A Gravity Forms account.'
                link='https://gravityforms.pxf.io/Qya3e9'
            />,
            <Requirement key='2' text='A WordPress website with the Gravity plug-in' />,
        ],
        supportedFields: [
            'Collect any Nutshell field via Gravity Form',
            'Match existing Nutshell people via email address',
        ],
        noteFromNutshellTeam: [
            <NutshellTeamNote
                key='1'
                feature='Landing pages'
                secondFeature='Nutshell Forms'
                integration='Gravity Forms'
            />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/gravity-forms-integrations-banner.webp',
    },
    {
        integrationId: 'housecall-pro',
        name: 'HouseCall Pro',
        primaryText: 'Create a customer or lead, or add to an audience',
        categoryId: ['communication'],
        integrationActionItems: [
            'Create a customer in HouseCall Pro when somebody is added to Nutshell',
            'Create a customer in HouseCall Pro when a lead is won in Nutshell',
            'Add a person to an audience when a HouseCall Pro job is completed',
            'Add a lead to Nutshell when a customer is added in HouseCall',
        ],
        integrationLogoUrl:
            '/include/images/integration-logos-new/housecall-pro-crm-integration.webp',
        requirements: [<Requirement key='1' text='A HouseCall Pro account' />],
        supportedFields: ['Name', 'Email address', 'Address', 'Phone', 'Tags'],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/housecall-pro-integrations-banner.webp',
    },
    {
        integrationId: 'marketing-cloud-fx',
        name: 'RevenueCloud FX',
        primaryText: 'Leverage your first-party data in Nutshell for stronger digital marketing',
        categoryId: ['other'],
        integrationActionItems: [
            'Calculate marketing ROI & customer lifetime value',
            'Sync CRM data to your online advertising campaigns',
        ],
        integrationLogoUrl:
            '/include/images/integration-logos-new/revenue-cloud-fx-crm-integration.webp',
        requirements: [
            <Requirement key='1' text='RevenueCloudFX is only available to WebFX customers.' />,
            <Requirement
                key='2'
                text='Not a customer? Learn more about the more about WebFX’s world-class marketing services and software solutions.'
            />,
        ],
        supportedFields: [
            'Customer name and contact information',
            'Company name',
            'Campaign and lead channel',
            'Lead value and pipeline status',
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/marketing-cloud-fx-integrations-banner.png',
    },
    {
        integrationId: 'jotform',
        name: 'Jotform',
        primaryText: 'Create leads and contacts, send drip campaigns',
        categoryId: ['forms'],
        integrationActionItems: [
            'Create a lead in Nutshell when a Jotform is submitted',
            'Create a contact in Nutshell when a Jotform is submitted',
            'Automatically send a Nutshell drip email when a Jotform is submitted',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/jotform-crm-integration.webp',
        requirements: [
            <Requirement
                key='1'
                text='A Jotform account.'
                link='https://link.jotform.com/nutshell-52qRNXcnUX'
            />,
        ],
        supportedFields: [
            'Collect any Nutshell field via Jotform',
            'Match existing Nutshell people via email address',
        ],
        noteFromNutshellTeam: [
            <NutshellTeamNote key='1' feature='Nutshell Forms' integration='Jotform' />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/jotform-integrations-banner.webp',
    },
    {
        integrationId: 'microsoft-teams',
        name: 'Microsoft Teams',
        primaryText: 'Get a Teams notification when a lead is won or created',
        categoryId: ['collaboration'],
        integrationActionItems: [
            'Get a Teams notification when a lead is won! 🥇',
            'Get a Teams notification when a lead is created',
            'Get a Teams notification when a specific activity type is completed',
        ],
        integrationLogoUrl:
            '/include/images/integration-logos-new/microsoft-teams-crm-integration.webp',
        requirements: [<Requirement key='1' text='A Microsoft Teams account' />],
        supportedFields: [
            'Lead name',
            'Phone  / Email / Address',
            'Lead value',
            'Pipeline',
            'Assignee',
            'Tags',
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/microsoft-teams-integrations-banner.webp',
    },
    {
        integrationId: 'pandadoc',
        name: 'PandaDoc',
        primaryText: 'Create a proposal, update leads and log activities without switching apps',
        categoryId: ['instant'],
        integrationActionItems: [
            'Create PandaDoc proposals from Nutshell leads',
            'Update a Nutshell lead when a PandaDoc document is signed',
            'Log a Nutshell activity when a PandaDoc document is signed',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/pandadoc-crm-integration.webp',
        requirements: [
            <Requirement
                key='1'
                text='A PandaDoc account.'
                link='https://www.pandadoc.com/?utm_medium=Affiliate&utm_source=willgordon7566&utm_campaign=Affiliate&ps_partner_key=d2lsbGdvcmRvbjc1NjY&ps_xid=VeQtzM1zfGMDuR&gsxid=VeQtzM1zfGMDuR&gspk=d2lsbGdvcmRvbjc1NjY'
            />,
        ],
        supportedFields: ['Name', 'Email', 'Address', 'Phone', 'Products', 'Lead value'],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/pandadoc-integrations-banner.webp',
    },
    {
        integrationId: 'quickbooks',
        name: 'QuickBooks',
        primaryText: 'Create a new QB customer or invoice, or mark a lead as won',
        categoryId: ['accounting', 'instant'],
        integrationActionItems: [
            'Create a new QuickBooks customer when a Nutshell lead is won',
            'Create a QuickBooks invoice when a Nutshell lead is won',
            'Mark a Nutshell lead as won when a QuickBooks invoice is paid',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/quickbooks-crm-integration.webp',
        requirements: [<Requirement key='1' text='A QuickBooks Online account' />],
        supportedFields: [
            'company & person name',
            'Phone / Email / Address',
            'Lead value',
            'Custom lead fields',
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/quickbooks-integrations-banner.png',
    },
    {
        integrationId: 'ringcentral',
        name: 'RingCentral',
        primaryText: 'Log a Nutshell activity when an SMS is sent in RingCentral',
        categoryId: ['instant', 'communication'],
        integrationActionItems: [
            'Log a Nutshell activity when an SMS is sent in RingCentral',
            'Create a RingCentral contact for new Nutshell leads',
        ],
        integrationLogoUrl:
            '/include/images/integration-logos-new/ringcentral-crm-integration.webp',
        requirements: [
            <Requirement key='1' text='A RingCentral account that’s capable of recording calls' />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/ringcentral-integrations-banner.png',
    },
    ...(NutClientConfig.hasRingcentralAppconnectFF
        ? [
              {
                  integrationId: 'ringcentral-appconnect',
                  name: 'RingCentral AppConnect',
                  primaryText:
                      'View Nutshell contacts and create activities through RingCentral AppConnect',
                  categoryId: ['instant', 'communication'],
                  integrationLogoUrl:
                      '/include/images/integration-logos-new/ringcentral-appconnect-integration.png',
              },
          ]
        : []),
    {
        integrationId: 'textmagic',
        name: 'TextMagic',
        primaryText: 'Send an SMS when a lead is created',
        categoryId: ['communication'],
        integrationActionItems: [
            'Send a TextMagic SMS when a lead is created in Nutshell',
            'Create a Nutshell note when an SMS is received from TextMagic',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/textmagic-crm-integration.webp',
        requirements: [
            <Requirement
                key='1'
                text='A TextMagic account.'
                link='https://www.textmagic.com/?fpr=nutshellcrm'
            />,
        ],
        supportedFields: ['Contact information'],
        noteFromNutshellTeam: [
            <NutshellTeamNote
                key='1'
                feature='SMS marketing'
                secondFeature='Nutshell SMS'
                integration='TextMagic'
            />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/textmagic-integrations-banner.png',
    },
    {
        integrationId: 'typeform',
        name: 'Typeform',
        primaryText: 'Create leads and contacts, send drip campaigns',
        categoryId: ['forms'],
        integrationActionItems: [
            'Create a lead in Nutshell when a Typeform is submitted',
            'Create a contact in Nutshell when a Typeform is submitted',
            'Automatically send a Nutshell drip email when a Typeform is submitted',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/typeform-crm-integration.webp',
        requirements: [<Requirement key='1' text='A TypeForm account' />],
        supportedFields: [
            'Collect any Nutshell field via Typeform',
            'Match existing Nutshell people via email address',
        ],
        noteFromNutshellTeam: [
            <NutshellTeamNote key='1' feature='Nutshell Forms' integration='Typeform' />,
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/typeform-integrations-banner.webp',
    },
    {
        integrationId: 'xero',
        name: 'Xero',
        primaryText: 'Create people, invoices and estimates without switching apps',
        categoryId: ['accounting'],
        integrationActionItems: [
            'Create Xero people when new Nutshell people are added',
            'Create a Xero invoice when a Nutshell lead is won',
            'Create a Xero estimate when a Nutshell lead is created',
        ],
        integrationLogoUrl: '/include/images/integration-logos-new/xero-crm-integration.webp',
        requirements: [<Requirement key='1' text='An active Xero account' />],
        supportedFields: [
            'Company & person name',
            'Phone / Email / Address',
            'Lead value',
            'Custom lead fields',
        ],
        integrationBannerUrl:
            '/include/images/integration-logo-banners/xero-integrations-banner.png',
    },
    {
        integrationId: 'google-workspace',
        name: 'Google Workspace',
        primaryText: 'Quickly add your whole team to Nutshell',
        categoryId: ['instant'],
        integrationLogoUrl: '/include/images/integration-logos-new/google-workspace.webp',
        requirements: [<Requirement key='1' text='A Google account' />],
    },
    {
        integrationId: 'microsoft-outlook',
        name: 'Microsoft Outlook',
        primaryText: 'Sync your emails and calendars with Nutshell',
        categoryId: ['instant'],
        integrationLogoUrl: '/include/images/integration-logos-new/microsoft.webp',
        requirements: [<Requirement key='1' text='A Microsoft Outlook account' />],
    },
    {
        integrationId: 'nutshell-campaigns',
        name: 'Nutshell Campaigns',
        primaryText: 'Send marketing messages to contacts in bulk, directly from Nutshell',
        categoryId: ['instant'],
        integrationLogoUrl: '/include/images/integration-logos-new/nutshell-marketing.webp',
        requirements: [<Requirement key='1' text='Nutshell Marketing' />],
    },
    {
        integrationId: 'mailchimp',
        name: 'MailChimp',
        primaryText: 'Make publishing, sending, and tracking email newsletters easy',
        categoryId: ['instant'],
        integrationLogoUrl: '/include/images/integration-logos-new/mailchimp.webp',
        requirements: [<Requirement key='1' text='A MailChimp account' />],
    },
    {
        integrationId: 'nutshell-forms',
        name: 'Nutshell Forms',
        primaryText:
            'Collect leads from your website, with custom fields, spam protection, and automation',
        categoryId: ['instant'],
        integrationLogoUrl: '/include/images/integration-logos-new/forms.webp',
    },
    {
        integrationId: 'wufoo',
        name: 'Wufoo',
        primaryText: 'Automatically create new leads in Nutshell from your Wufoo forms',
        categoryId: ['instant'],
        integrationLogoUrl: '/include/images/integration-logos-new/wufoo.webp',
    },
    {
        integrationId: 'wordpress',
        name: 'WordPress',
        primaryText: 'Use Nutshell Analytics with a WordPress website',
        categoryId: ['instant'],
        integrationLogoUrl: '/include/images/integration-logos-new/wordpress.webp',
    },
    {
        integrationId: 'send-to',
        name: 'Send To',
        primaryText: 'Open Nutshell leads in other software',
        categoryId: ['instant'],
        integrationLogoUrl: '/include/images/integration/nutshell-mail.png',
    },
    {
        integrationId: 'nutshell-scheduler',
        name: 'Nutshell Scheduler',
        primaryText: 'Send a link so contacts can book time directly on your calendar',
        categoryId: ['instant'],
        integrationLogoUrl: '/include/images/integration-logos-new/nutshell-scheduler.webp',
    },
    {
        integrationId: 'slack',
        name: 'Slack',
        primaryText: 'Get custom Slack notifications as leads move through your pipeline',
        categoryId: ['instant'],
        integrationLogoUrl: '/include/images/integration-logos-new/slack.webp',
    },
    {
        integrationId: 'zendesk',
        name: 'Zendesk',
        primaryText: 'Automaticallly import your support tickets into Nutshell',
        categoryId: ['instant'],
        integrationLogoUrl: '/include/images/integration-logos-new/zendesk.webp',
    },
    {
        integrationId: 'intercom',
        name: 'Intercom',
        primaryText: 'Automaticallly import your Intercom chats into Nutshell',
        categoryId: ['instant'],
        integrationLogoUrl: '/include/images/integration-logos-new/intercom.webp',
    },
    {
        integrationId: 'olark',
        name: 'Olark',
        primaryText: 'Chat live with your web visitors and save the conversation in Nutshell',
        categoryId: ['instant'],
        integrationLogoUrl: '/include/images/integration-logos-new/olark.webp',
    },
    {
        integrationId: 'uservoice',
        name: 'UserVoice',
        primaryText: 'Track the conversations you have in UserVoice tickets',
        categoryId: ['instant'],
        integrationLogoUrl: '/include/images/integration-logos-new/uservoice.webp',
    },
    {
        integrationId: 'krispcall',
        name: 'KrispCall',
        primaryText: 'Automatically log Nutshell activities from phone calls made on KrispCall',
        categoryId: ['instant'],
        integrationLogoUrl: '/include/images/integration-logos-new/krispcall.webp',
        integrationBannerUrl:
            '/include/images/integration-logo-banners/xero-integrations-banner.png',
    },
    {
        integrationId: 'nutshell-smsmarketing',
        name: 'SMS Marketing',
        primaryText: 'Send SMS marketing messages to your audiences directly from Nutshell.',
        categoryId: ['instant'],
        integrationLogoUrl: '/include/images/integration-logos-new/nutshell-smsmarketing.webp',
    },
    {
        integrationId: 'nutshell-iq',
        name: 'Nutshell IQ',
        primaryText: 'Prospect for new sales opportunities directly in Nutshell.',
        categoryId: ['instant', 'lead_generation'],
        integrationLogoUrl: '/include/images/integration-logos-new/nutshell-iq.webp',
    },
    {
        integrationId: 'nutshell-quotes',
        name: 'Nutshell Quotes & Invoices',
        primaryText: 'Create and send quotes & invoices directly in Nutshell.',
        categoryId: ['instant', 'contracts_proposals'],
        integrationLogoUrl: '/include/images/integration-logos-new/nutshell-quotes.webp',
    },
    {
        integrationId: 'nutshell-webchat',
        name: 'Web Chat',
        primaryText: 'Chat with customers and leads via chatbot on your site.',
        categoryId: ['instant'],
        integrationLogoUrl: '/include/images/integration-logos-new/nutshell-webchat.webp',
    },
    {
        integrationId: 'nutshell-sms',
        name: 'SMS',
        primaryText: 'Send and receive 1-to-1 SMS messages directly from Nutshell.',
        categoryId: ['instant', 'communication'],
        integrationLogoUrl: '/include/images/integration-logos-new/nutshell-sms.webp',
    },
    {
        integrationId: 'facebook-messenger',
        name: 'Facebook Messenger',
        primaryText:
            'Connect your business’s Facebook page, and reply to messages directly from Nutshell.',
        categoryId: ['instant', 'communication'],
        integrationLogoUrl: '/include/images/integration-logos-new/messenger.png',
    },
    {
        integrationId: 'whatsapp',
        name: 'WhatsApp',
        primaryText:
            'Connect your WhatsApp business account, and reply to messages directly from Nutshell.',
        categoryId: ['instant', 'communication'],
        integrationLogoUrl: '/include/images/integration-logos-new/whatsapp.png',
    },
    {
        integrationId: 'instagram',
        name: 'Instagram DMs',
        primaryText:
            'Connect your business’s Instagram account, and reply to messages directly from Nutshell.',
        categoryId: ['instant', 'communication'],
        integrationLogoUrl: '/include/images/integration-logos-new/instagram.png',
    },
] as const;
